import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, XCircle, AlertTriangle } from 'lucide-react'
import {
  RegexRuleCreateInputSchema,
  type ClassGoldExample,
  type RegexRule,
  type RegexRuleCreateInput,
} from '@sophia/shared-schemas'
import { Drawer } from '@/components/ui/Drawer'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Field } from '@/components/ui/Field'
import { Textarea } from '@/components/ui/Textarea'
import { Badge } from '@/components/ui/Badge'
import { Switch } from '@/components/ui/Switch'
import { classesApi, classesQueryKey } from '../api'
import { extractApiError, type NormalizedError } from '@/lib/api'
import { cn } from '@/lib/utils'

interface Props {
  open: boolean
  onClose: () => void
  classId: string
  goldExamples: ClassGoldExample[]
  /** Se passar uma regra, abre em modo edição */
  rule?: RegexRule | null
}

interface PreviewMatch {
  exampleIdx: number
  matched: boolean
  excerpt: string
}

interface PreviewResult {
  ok: boolean
  error?: string
  matches: PreviewMatch[]
  hits: number
  total: number
}

function compileAndTest(pattern: string, flags: string, examples: ClassGoldExample[]): PreviewResult {
  if (!pattern) {
    return { ok: false, error: 'Padrão vazio', matches: [], hits: 0, total: examples.length }
  }
  let re: RegExp
  try {
    re = new RegExp(pattern, flags)
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Padrão inválido',
      matches: [],
      hits: 0,
      total: examples.length,
    }
  }
  const matches: PreviewMatch[] = examples.map((ex, idx) => {
    const matcher = flags.includes('g') ? new RegExp(pattern, flags) : re
    const m = matcher.exec(ex.text)
    if (m) {
      const start = Math.max(0, (m.index ?? 0) - 30)
      const end = Math.min(ex.text.length, (m.index ?? 0) + (m[0]?.length ?? 0) + 30)
      const excerpt =
        (start > 0 ? '…' : '') + ex.text.slice(start, end) + (end < ex.text.length ? '…' : '')
      return { exampleIdx: idx, matched: true, excerpt }
    }
    return { exampleIdx: idx, matched: false, excerpt: '' }
  })
  return {
    ok: true,
    matches,
    hits: matches.filter((m) => m.matched).length,
    total: examples.length,
  }
}

export function RuleFormDrawer({ open, onClose, classId, goldExamples, rule }: Props) {
  const qc = useQueryClient()
  const isEdit = !!rule
  const [error, setError] = useState<NormalizedError | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegexRuleCreateInput>({
    resolver: zodResolver(RegexRuleCreateInputSchema),
    defaultValues: {
      name: '',
      pattern: '',
      flags: 'i',
      weight: 10,
      enabled: true,
    },
  })

  // Sync form when rule changes (edit mode) or when opening fresh (create mode)
  useEffect(() => {
    if (!open) return
    if (rule) {
      reset({
        name: rule.name,
        pattern: rule.pattern,
        flags: rule.flags,
        weight: rule.weight,
        enabled: rule.enabled,
      })
    } else {
      reset({ name: '', pattern: '', flags: 'i', weight: 10, enabled: true })
    }
    setError(null)
  }, [open, rule, reset])

  const pattern = watch('pattern') ?? ''
  const flags = watch('flags') ?? 'i'
  const preview = compileAndTest(pattern.trim(), flags, goldExamples)

  const createMut = useMutation({
    mutationFn: (data: RegexRuleCreateInput) => classesApi.createRule(classId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: classesQueryKey.detail(classId) })
      onClose()
    },
    onError: (err) => setError(extractApiError(err)),
  })

  const updateMut = useMutation({
    mutationFn: (data: RegexRuleCreateInput) =>
      classesApi.updateRule(classId, rule!.id, {
        name: data.name,
        pattern: data.pattern,
        flags: data.flags,
        weight: data.weight,
        enabled: data.enabled,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: classesQueryKey.detail(classId) })
      onClose()
    },
    onError: (err) => setError(extractApiError(err)),
  })

  const isPending = createMut.isPending || updateMut.isPending
  const fieldErrors = error?.fieldErrors ?? {}

  function onSubmit(data: RegexRuleCreateInput) {
    setError(null)
    if (isEdit) updateMut.mutate(data)
    else createMut.mutate(data)
  }

  return (
    <Drawer
      open={open}
      onClose={isPending ? () => {} : onClose}
      title={isEdit ? 'Editar regra' : 'Nova regra manual'}
      description={
        isEdit
          ? 'Ajuste o padrão, nome ou flags. Cobertura é recalculada automaticamente.'
          : 'Crie uma regra direto com regex. Use isso quando quiser controle fino sem passar pelo LLM.'
      }
      width={680}
      closeOnOverlay={!isPending}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            loading={isPending}
            disabled={!preview.ok}
          >
            {isEdit ? 'Salvar alterações' : 'Criar regra'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
        <Field
          label="Nome da regra"
          htmlFor="rule-name"
          required
          hint="Descreva em PT-BR o que essa regra busca."
          error={errors.name?.message ?? fieldErrors.name?.[0]}
        >
          <Input
            id="rule-name"
            placeholder="Ex: Cabeçalho 'Audiência de Instrução'"
            autoFocus
            invalid={!!errors.name || !!fieldErrors.name}
            {...register('name')}
          />
        </Field>

        <Field
          label="Padrão regex"
          htmlFor="rule-pattern"
          required
          hint="Sintaxe JavaScript. Não escape o /. Use \\b pra delimitar palavras."
          error={errors.pattern?.message ?? fieldErrors.pattern?.[0]}
        >
          <Textarea
            id="rule-pattern"
            rows={3}
            className="data-mono text-sm"
            placeholder={'audi[eê]ncia\\s+de\\s+instru[cç][aã]o'}
            invalid={!!errors.pattern || !!fieldErrors.pattern || !preview.ok}
            {...register('pattern')}
          />
        </Field>

        <div className="grid grid-cols-3 gap-4">
          <Field
            label="Flags"
            htmlFor="rule-flags"
            hint="i m s u y g"
            error={errors.flags?.message ?? fieldErrors.flags?.[0]}
          >
            <Input
              id="rule-flags"
              className="data-mono"
              maxLength={6}
              invalid={!!errors.flags || !!fieldErrors.flags}
              {...register('flags')}
            />
          </Field>
          <Field
            label="Peso"
            htmlFor="rule-weight"
            hint="1-100, define prioridade"
            error={errors.weight?.message ?? fieldErrors.weight?.[0]}
          >
            <Input
              id="rule-weight"
              type="number"
              min={1}
              max={100}
              invalid={!!errors.weight || !!fieldErrors.weight}
              {...register('weight', { valueAsNumber: true })}
            />
          </Field>
          <Field label="Status" hint="Aplica em produção?">
            <div className="inline-flex items-center gap-3 h-10 px-3 rounded-md border border-border bg-bg-surface">
              <Switch
                checked={watch('enabled') ?? true}
                onCheckedChange={(v) => setValue('enabled', v, { shouldDirty: true })}
                label={watch('enabled') ? 'Desativar' : 'Ativar'}
              />
              <span className="text-sm text-fg-2">{watch('enabled') ? 'Ativa' : 'Desativada'}</span>
            </div>
          </Field>
        </div>

        {/* Live preview */}
        <div className="rounded-md border border-border-subtle bg-bg-subtle p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="data-mono text-2xs uppercase tracking-wider text-fg-3">
              Preview contra exemplos-padrão
            </span>
            {preview.ok ? (
              <Badge tone={preview.hits === preview.total ? 'success' : preview.hits > 0 ? 'warning' : 'danger'}>
                <span className="data-mono">
                  {preview.hits}/{preview.total}
                </span>
              </Badge>
            ) : (
              <Badge tone="danger">
                <AlertTriangle className="h-3 w-3" />
                inválido
              </Badge>
            )}
          </div>

          {!preview.ok && preview.error && (
            <p className="text-xs text-danger data-mono">{preview.error}</p>
          )}

          {preview.ok && goldExamples.length === 0 && (
            <p className="text-xs text-fg-3 italic">
              Nenhum exemplo-padrão cadastrado. Adicione exemplos pra ver a cobertura ao vivo.
            </p>
          )}

          {preview.ok && preview.matches.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {preview.matches.map((m) => (
                <li
                  key={m.exampleIdx}
                  className={cn(
                    'flex items-start gap-2 text-xs px-2 py-1.5 rounded border',
                    m.matched
                      ? 'border-success/20 bg-success-bg/50 text-fg-2'
                      : 'border-border-subtle bg-bg-surface text-fg-3',
                  )}
                >
                  {m.matched ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-fg-3 shrink-0 mt-0.5" />
                  )}
                  <span className="data-mono text-2xs text-fg-3 mt-0.5">#{m.exampleIdx + 1}</span>
                  <span className="flex-1 line-clamp-2">
                    {m.matched ? m.excerpt : <span className="italic">não casou</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && Object.entries(fieldErrors).length === 0 && (
          <div className="text-sm text-danger bg-danger-bg border border-danger/30 rounded-md px-3 py-2">
            {error.message}
          </div>
        )}
      </form>
    </Drawer>
  )
}
